import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Card, Button, Input } from '../components';
import { Building2, User, Mail, Phone, MapPin, Globe } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const screeningSchema = z.object({
  bedrijfsnaam: z.string().min(2, 'Bedrijfsnaam is verplicht'),
  kvk_nummer: z.string().length(8, 'KvK nummer moet 8 cijfers zijn'),
  btw_nummer: z.string().optional(),
  bedrijfsadres: z.string().min(5, 'Bedrijfsadres is verplicht'),
  postcode: z.string().regex(/^[1-9][0-9]{3}\s?[A-Z]{2}$/i, 'Ongeldige postcode'),
  plaats: z.string().min(2, 'Plaats is verplicht'),
  website: z.string().url('Ongeldige URL').optional().or(z.literal('')),
  telefoon: z.string().optional(),
  email: z.string().email('Ongeldig e-mailadres'),
  contactpersoon_naam: z.string().min(2, 'Naam contactpersoon is verplicht'),
  contactpersoon_functie: z.string().optional(),
  contactpersoon_email: z.string().email('Ongeldig e-mailadres'),
  contactpersoon_telefoon: z.string().optional(),
});

type ScreeningFormData = z.infer<typeof screeningSchema>;

export const EmployerScreeningPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScreeningFormData>({
    resolver: zodResolver(screeningSchema),
  });

  const submitScreeningMutation = useMutation({
    mutationFn: async (data: ScreeningFormData) => {
      const response = await api.post('/employer/screening', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('screening.submitSuccess'));
      navigate('/dashboard');
    },
    onError: (error: any) => {
      console.error('Screening submission error:', error);
      toast.error(error.response?.data?.message || t('screening.submitError'));
    },
  });

  const onSubmit = (data: ScreeningFormData) => {
    submitScreeningMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <img
            src="/belagom-text.png"
            alt="BeLagom"
            className="h-12 mx-auto cursor-pointer mb-6"
            onClick={() => navigate('/')}
          />
          <h1 className="text-3xl font-bold text-gray-900">{t('screening.title')}</h1>
          <p className="mt-2 text-gray-600">
            {t('screening.subtitle')}
          </p>
        </div>

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
            {/* Bedrijfsgegevens */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Building2 className="w-6 h-6 mr-2" />
                {t('screening.companyDetails')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('screening.companyName')}
                  icon={<Building2 />}
                  error={errors.bedrijfsnaam?.message}
                  {...register('bedrijfsnaam')}
                />

                <Input
                  label={t('screening.kvkNumber')}
                  icon={<Building2 />}
                  error={errors.kvk_nummer?.message}
                  {...register('kvk_nummer')}
                  maxLength={8}
                  placeholder="12345678"
                />

                <Input
                  label={t('screening.btwNumber')}
                  icon={<Building2 />}
                  error={errors.btw_nummer?.message}
                  {...register('btw_nummer')}
                />

                <Input
                  label={t('screening.website')}
                  icon={<Globe />}
                  error={errors.website?.message}
                  {...register('website')}
                  placeholder="https://www.voorbeeld.nl"
                />
              </div>

              <div className="mt-4">
                <Input
                  label={t('screening.companyAddress')}
                  icon={<MapPin />}
                  error={errors.bedrijfsadres?.message}
                  {...register('bedrijfsadres')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  label={t('screening.postalCode')}
                  icon={<MapPin />}
                  error={errors.postcode?.message}
                  {...register('postcode')}
                  placeholder="1234 AB"
                />

                <Input
                  label={t('screening.city')}
                  icon={<MapPin />}
                  error={errors.plaats?.message}
                  {...register('plaats')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Input
                  label={t('screening.phone')}
                  icon={<Phone />}
                  error={errors.telefoon?.message}
                  {...register('telefoon')}
                />

                <Input
                  label={t('screening.email')}
                  icon={<Mail />}
                  error={errors.email?.message}
                  {...register('email')}
                />
              </div>
            </div>

            {/* Contactpersoon */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <User className="w-6 h-6 mr-2" />
                {t('screening.contactPerson')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('screening.contactName')}
                  icon={<User />}
                  error={errors.contactpersoon_naam?.message}
                  {...register('contactpersoon_naam')}
                />

                <Input
                  label={t('screening.contactFunction')}
                  icon={<User />}
                  error={errors.contactpersoon_functie?.message}
                  {...register('contactpersoon_functie')}
                />

                <Input
                  label={t('screening.contactEmail')}
                  icon={<Mail />}
                  error={errors.contactpersoon_email?.message}
                  {...register('contactpersoon_email')}
                />

                <Input
                  label={t('screening.contactPhone')}
                  icon={<Phone />}
                  error={errors.contactpersoon_telefoon?.message}
                  {...register('contactpersoon_telefoon')}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                {t('screening.cancel')}
              </Button>
              <Button
                type="submit"
                loading={submitScreeningMutation.isPending}
              >
                {t('screening.submit')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
